import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import { ButtonComponent } from "../../../components/elements";
import { Modal, Row, Col, Form, Pagination, Table } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import { Link, useNavigate } from "react-router-dom";
import Swal from 'sweetalert2';
import '../App/modalstyle.css';

export default function DailyExpenseIndex() {
    const { t } = useContext(TranslatorContext);
    const navigate = useNavigate();

    const permissions = JSON.parse(localStorage.getItem("permissions")) || [];

    const hasPermission = (perm) => {
        return permissions.includes(perm);
    };

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [alertModal, setAlertModal] = useState(false);
    const [currentId, setCurrentId] = useState(null);

    // Filter states
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [selectedDate, setSelectedDate] = useState('');
    const [dateFilterType, setDateFilterType] = useState('range');

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [total, setTotal] = useState(0);
    const [lastPage, setLastPage] = useState(1);

    useEffect(() => {
        fetchDailyExpenses();
    }, [currentPage, perPage]);

    const fetchDailyExpenses = () => {
        setLoading(true);
        const token = localStorage.getItem("authToken");

        let url = `${process.env.REACT_APP_API_URL}/daily-expenses?page=${currentPage}&per_page=${perPage}`;

        if (dateFilterType === 'range' && fromDate && toDate) {
            url += `&from_date=${fromDate}&to_date=${toDate}`;
        } else if (dateFilterType === 'single' && selectedDate) {
            url += `&date=${selectedDate}`;
        }

        axios.get(url, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        })
            .then(res => {
                if (res.data.dailyExepense) {
                    setData(res.data.dailyExepense.data || []);
                    setTotal(res.data.dailyExepense.total || 0);
                    setCurrentPage(res.data.dailyExepense.current_page || 1);
                    setLastPage(res.data.dailyExepense.last_page || 1);
                }
            })
            .catch(err => console.error(err))
            .finally(() => setLoading(false));
    };

    const handleFilter = () => {
        setCurrentPage(1);
        fetchDailyExpenses();
    };

    const handleReset = () => {
        setFromDate('');
        setToDate('');
        setSelectedDate('');
        setDateFilterType('range');
        setCurrentPage(1);
        setTimeout(() => {
            fetchDailyExpenses();
        }, 100);
    };

    const handleDelete = async (id) => {
        const token = localStorage.getItem("authToken");

        try {
            await axios.delete(`${process.env.REACT_APP_API_URL}/daily-expenses/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setAlertModal(false);
            fetchDailyExpenses();

            Swal.fire({
                icon: 'success',
                title: t('success'),
                text: t('Daily expense deleted successfully'),
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true
            });
        } catch (error) {
            console.error('Error deleting daily expense:', error);
            Swal.fire({
                icon: 'error',
                title: t('error'),
                text: error.response?.data?.message || t('Failed to delete'),
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true
            });
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString();
        } catch {
            return 'N/A';
        }
    };

    const formatCurrency = (amount) => {
        if (!amount) return '0';
        return parseFloat(amount).toFixed(2);
    };

    // Generate pagination items
    const getPaginationItems = () => {
        let items = [];
        let startPage = Math.max(1, currentPage - 2);
        let endPage = Math.min(lastPage, currentPage + 2);

        if (endPage - startPage < 4) {
            if (startPage > 1) {
                startPage = Math.max(1, endPage - 4);
            }
            if (endPage < lastPage) {
                endPage = Math.min(lastPage, startPage + 4);
            }
        }

        // First page button
        if (startPage > 1) {
            items.push(
                <Pagination.First key="first" onClick={() => handlePageChange(1)} />
            );
            items.push(<Pagination.Ellipsis key="ellipsis-start" />);
        }

        // Page numbers
        for (let page = startPage; page <= endPage; page++) {
            items.push(
                <Pagination.Item
                    key={page}
                    active={page === currentPage}
                    onClick={() => handlePageChange(page)}
                >
                    {page}
                </Pagination.Item>
            );
        }

        // Last page button
        if (endPage < lastPage) {
            items.push(<Pagination.Ellipsis key="ellipsis-end" />);
            items.push(
                <Pagination.Last key="last" onClick={() => handlePageChange(lastPage)} />
            );
        }

        return items;
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= lastPage && newPage !== currentPage) {
            setCurrentPage(newPage);
        }
    };

    if (loading && data.length === 0) return <PageLayout><p>{t("loading")}...</p></PageLayout>;

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card">
                        <div className="mc-breadcrumb">
                            <h3 className="mc-breadcrumb-title">{t('daily expenses')}</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">{t('home')}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">{t('expenses')}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">{t('daily expenses')}</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                <Col xl={12}>
                    <div className="mc-card">
                        {/* Header with Add Button */}
                        <div className="mc-card-header d-flex justify-content-between align-items-center p-3">
                            <h5 className="mc-card-title mb-0">{t('daily expenses list')}</h5>
                            {hasPermission("Daily Expenses create") && (
                                <ButtonComponent
                                    type="button"
                                    className="btn btn-primary d-flex align-items-center"
                                    onClick={() => navigate(`/daily-expense-create`)}
                                >
                                    <i className="material-icons">add</i>
                                    {t('Add expense')}
                                </ButtonComponent>
                            )}
                        </div>

                        {/* Filter Section */}
                        <div className="p-3 border-bottom">
                            <Row className="align-items-end">
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label className="fw-bold">{t('filter type')}</Form.Label>
                                        <Form.Select
                                            value={dateFilterType}
                                            onChange={(e) => setDateFilterType(e.target.value)}
                                            className="custom-input"
                                        >
                                            <option value="range">{t('Date range')}</option>
                                            <option value="single">{t('Single date')}</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>

                                {dateFilterType === 'range' ? (
                                    <>
                                        <Col md={3}>
                                            <Form.Group>
                                                <Form.Label className="fw-bold">{t('from date')}</Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    value={fromDate}
                                                    onChange={(e) => setFromDate(e.target.value)}
                                                    className="custom-input"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group>
                                                <Form.Label className="fw-bold">{t('to date')}</Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    value={toDate}
                                                    onChange={(e) => setToDate(e.target.value)}
                                                    className="custom-input"
                                                />
                                            </Form.Group>
                                        </Col>
                                    </>
                                ) : (
                                    <Col md={3}>
                                        <Form.Group>
                                            <Form.Label className="fw-bold">{t('date')}</Form.Label>
                                            <Form.Control
                                                type="date"
                                                value={selectedDate}
                                                onChange={(e) => setSelectedDate(e.target.value)}
                                                className="custom-input"
                                            />
                                        </Form.Group>
                                    </Col>
                                )}

                                <Col md={3}>
                                    <div className="d-flex gap-2">
                                        <ButtonComponent
                                            type="button"
                                            className="btn btn-primary"
                                            onClick={handleFilter}
                                        >
                                            <i className="material-icons me-1" style={{ fontSize: '16px' }}>search</i>
                                            {t('filter')}
                                        </ButtonComponent>
                                        <ButtonComponent
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={handleReset}
                                        >
                                            <i className="material-icons me-1" style={{ fontSize: '16px' }}>refresh</i>
                                            {t('reset')}
                                        </ButtonComponent>
                                    </div>
                                </Col>
                            </Row>
                        </div>

                        <Row>
                            <Col xl={12}>
                                <div className="mc-card">
                                    <div className="table-responsive-wrapper">
                                        <Table responsive className="table mb-0">
                                            <thead>
                                                <tr>
                                                    <th>
                                                        <div className="mc-table-check">
                                                            <p>#</p>
                                                        </div>
                                                    </th>
                                                    <th>{t('date')}</th>
                                                    <th>{t('category')}</th>
                                                    <th>{t('amount')}</th>
                                                    <th>{t('actions')}</th>
                                                </tr>
                                            </thead>
                                            <tbody className="mc-table-body even">
                                                {data.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="5" className="text-center py-5">
                                                            <i className="material-icons" style={{ fontSize: '48px' }}>inbox</i>
                                                            <p className="mt-2">{t('no expenses found')}</p>
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    data.map((item, index) => (
                                                        <tr key={item.id}>
                                                            <td>
                                                                <div className="mc-table-check">
                                                                    <p>{((currentPage - 1) * perPage) + index + 1}</p>
                                                                </div>
                                                            </td>
                                                            <td>{formatDate(item.date)}</td>
                                                            <td>
                                                                {item.details && item.details.length > 0 ? (
                                                                    item.details.map((detail, idx) => (
                                                                        <span key={idx} className="badge badge-primary me-1 text-dark d-inline-block mb-1">
                                                                            {detail.category?.name || 'N/A'}
                                                                        </span>
                                                                    ))
                                                                ) : (
                                                                    <span className="badge badge-secondary">-</span>
                                                                )}
                                                            </td>
                                                            <td>
                                                                {item.details && item.details.length > 0 ? (
                                                                    formatCurrency(item.details.reduce((sum, detail) => sum + parseFloat(detail.amount || 0), 0))
                                                                ) : (
                                                                    '0'
                                                                )}
                                                            </td>
                                                            <td>
                                                                <div className="mc-table-action">
                                                                    {hasPermission("Daily Expenses edit") && (
                                                                        <ButtonComponent
                                                                            type="button"
                                                                            className="material-icons edit"
                                                                            onClick={() => navigate(`/daily-expense-edit/${item.id}`)}
                                                                            title={t('edit')}
                                                                        >
                                                                            edit
                                                                        </ButtonComponent>
                                                                    )}
                                                                    {/* <ButtonComponent
                                                                        type="button"
                                                                        className="material-icons delete"
                                                                        onClick={() => {
                                                                            setCurrentId(item.id);
                                                                            setAlertModal(true);
                                                                        }}
                                                                        title={t('delete')}
                                                                    >
                                                                        delete
                                                                    </ButtonComponent> */}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </Table>

                                        {/* Bootstrap Pagination */}
                                        {total > 0 && (
                                            <div className="d-flex justify-content-between align-items-center p-3 border-top">
                                                <div className="text-muted small">
                                                    {t('showing')} {((currentPage - 1) * perPage) + 1} - {Math.min(currentPage * perPage, total)} {t('of')} {total} {t('entries')}
                                                </div>
                                                <div className="d-flex gap-3 align-items-center">

                                                    <Pagination size="sm" className="mb-0">
                                                        <Pagination.Prev
                                                            onClick={() => handlePageChange(currentPage - 1)}
                                                            disabled={currentPage === 1}
                                                        />
                                                        {getPaginationItems()}
                                                        <Pagination.Next
                                                            onClick={() => handlePageChange(currentPage + 1)}
                                                            disabled={currentPage === lastPage}
                                                        />
                                                    </Pagination>
                                                </div>
                                            </div>
                                        )}

                                        {/* Delete Confirmation Modal */}
                                        <Modal show={alertModal} onHide={() => setAlertModal(false)} centered>
                                            <div className="mc-alert-modal p-4 text-center">
                                                <i className="material-icons" style={{ fontSize: '50px', color: 'red' }}>warning</i>
                                                <h3 className="mt-3">{t("Are you sure?")}</h3>
                                                <p>{t("Want to delete this daily expense?")}</p>
                                                <div className="d-flex justify-content-center gap-2 mt-3">
                                                    <ButtonComponent
                                                        type="button"
                                                        className="btn btn-secondary"
                                                        onClick={() => setAlertModal(false)}
                                                    >
                                                        {t('cancel')}
                                                    </ButtonComponent>
                                                    <ButtonComponent
                                                        type="button"
                                                        className="btn btn-danger"
                                                        onClick={() => handleDelete(currentId)}
                                                    >
                                                        {t('delete')}
                                                    </ButtonComponent>
                                                </div>
                                            </div>
                                        </Modal>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </div>
                </Col>
            </Row>
        </PageLayout>
    );
}